import { CancelIcon } from '@chegg-ui/theme'
import {
  hideModal,
  ModalNames,
  ONE_AUTH_MODAL,
  PAYMENT_MODAL,
} from '@src/state/modals/actions'
import { RootState } from '@src/state/types'
import easybib from '@src/styled/theme'
import { getTestIdOnModalClose } from '@src/utils/dataTestIds/getters'
import noop from '@src/utils/noop'
import * as React from 'react'
import { Modal as ReactOverlayModal } from 'react-overlays'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import styled, { css } from 'styled-components'
import { UnstyledButton } from '../base/Buttons'

const modalStyle = {
  position: 'fixed',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
}

const baseBackdropStyle = {
  ...modalStyle,
  backgroundColor: easybib.colors.grey,
  opacity: 0.5,
  zIndex: 300,
}

interface BaseModalProps {
  dismissible?: boolean
  onBackdropClick?: () => void
  backdropStyle?: object
  children?: JSX.Element
  name: ModalNames
  testId?: string
}

const Modal = styled<any>(ReactOverlayModal)`
  z-index: 200;
`

interface CloseIconProps {
  color: string
  width: number
  onClick: () => void
  ['data-test']: string
  // styled-component
  className?: any
}

export const BaseCloseIcon: React.FunctionComponent<CloseIconProps> = ({
  onClick,
  className,
  ...rest
}) => (
  <UnstyledButton onClick={onClick} className={className}>
    <CancelIcon {...rest} size="medium" />
  </UnstyledButton>
)

const CloseIcon = styled(BaseCloseIcon)`
  display: block;
  z-index: 30000;
  top: -38px;
  right: -8px;
  position: absolute;
  &:hover {
    cursor: pointer;
  }
`

const PaymentModalCloseIcon = styled(BaseCloseIcon)`
  display: block;
  z-index: 30000;
  top: 10px;
  right: 15px;
  position: absolute;
  &:hover {
    cursor: pointer;
  }
`

const PositioningWrapper = styled.div`
  display: flex;
  width: 100vw;
  height: 100vh;
  justify-content: center;
  align-items: center;
`

const InnerWrapper = styled.div<
  Pick<BaseModalProps, 'dismissible' | 'name'> & StateProps
>`
  font-weight: 300;
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${props =>
    props.name === ONE_AUTH_MODAL ? `rgba(0,0,0,0)` : props.theme.colors.white};
  z-index: 3000;
  ${props =>
    props.name === ONE_AUTH_MODAL
      ? css`
          overflow-y: auto;
          max-height: 100%;
        `
      : ''};
  @media (max-width: 656px) {
    ${props =>
      props.dismissible &&
      !(props.name === PAYMENT_MODAL) &&
      !(props.name === ONE_AUTH_MODAL)
        ? // give margin for X icon
          css`
            margin-top: ${props.theme.spacing.xl};
          `
        : ''}
    width: 100%;
    ${props =>
      props.name === ONE_AUTH_MODAL
        ? css`
            max-height: 100%;
          `
        : 'height: 100%;'};
  }
  @media screen and (max-width: 899px), (max-height: 630px) {
    ${props =>
      props.name === PAYMENT_MODAL
        ? css`
            height: 100%;
            width: unset;
            background: transparent;
            overflow: scroll;
          `
        : ''}
    ${props =>
      props.name === ONE_AUTH_MODAL
        ? css`
            max-height: 100%;
            width: auto;
            background: transparent;
            overflow: auto;
          `
        : ''}
  }
`

interface StateProps {
  show?: boolean
}

interface DispatchProps {
  hideModal: (origin?: string) => void
}

const defaultProps: DispatchProps = {
  hideModal: noop,
}

type Props = BaseModalProps & StateProps & DispatchProps

const shouldShow = (modals: object, name: string) => modals.hasOwnProperty(name)

export const BaseModal: React.FunctionComponent<Props> = props => {
  const { dismissible, children, show, testId, onBackdropClick, hideModal, name } = props
  if (!show) {
    return null
  }

  const backdropStyle = {
    ...baseBackdropStyle,
    ...props.backdropStyle,
  }

  const handleBackdropClick = () => {
    if (onBackdropClick) {
      onBackdropClick()
    } else {
      hideModal()
    }
  }

  const isPaymentModal = name === PAYMENT_MODAL
  const isOneAuthModal = name === ONE_AUTH_MODAL

  const closeIconProps = {
    color: isPaymentModal || isOneAuthModal ? easybib.colors.grey : easybib.colors.white,
    width: 42,
    onClick: hideModal,
    ['data-test']: getTestIdOnModalClose(name),
  }

  return (
    <Modal
      show={true}
      onBackdropClick={dismissible ? handleBackdropClick : noop}
      backdropStyle={backdropStyle}
      data-test={name}
    >
      <PositioningWrapper>
        <InnerWrapper data-test={testId} name={name} dismissible={dismissible}>
          {dismissible &&
            (isPaymentModal || isOneAuthModal ? (
              <PaymentModalCloseIcon {...closeIconProps} />
            ) : (
              <CloseIcon {...closeIconProps} />
            ))}
          {children}
        </InnerWrapper>
      </PositioningWrapper>
    </Modal>
  )
}

BaseModal.defaultProps = defaultProps

const mapStateToProps = (state: RootState, ownProps: Props): StateProps => ({
  show: shouldShow(state.modals, ownProps.name),
})

const mapDispatchToProps = (dispatch: Dispatch, ownProps: Props): DispatchProps => ({
  hideModal: () =>
    dispatch(
      hideModal({ name: ownProps.name as ModalNames }, { origin: 'close modal x' })
    ),
})

export default connect<StateProps, DispatchProps, BaseModalProps>(
  mapStateToProps,
  mapDispatchToProps
)(BaseModal)



// WEBPACK FOOTER //
// ./src/components/Modals/BaseModal.tsx